/* eslint-disable no-restricted-imports */
import { useEffect, useEffectEvent } from "react";
import { useQuery as originalUseQuery, UseQueryOptions as OriginalUseQueryOptions } from "@tanstack/react-query";

import { ServiceClientHttpError } from "../../../../commons/libs/client-utils";

export const shouldRetryQuery = (failureCount: number, error: unknown): boolean => {
  const status = error instanceof ServiceClientHttpError ? error.status : undefined;
  const isPermanentClientError =
    status !== undefined && status >= 400 && status < 500 && status !== 408 && status !== 429;

  return !isPermanentClientError && failureCount < 3;
};

export type UseQueryOptions<TQueryFnData = unknown, TError = Error> = OriginalUseQueryOptions<TQueryFnData, TError> & {
  onSuccess?: (data: TQueryFnData) => void;
  onData?: (data: TQueryFnData) => void;
  onError?: (error: TError) => void;
};

const useQuery = <Data = unknown, Err = Error>(options: UseQueryOptions<Data, Err>) => {
  const query = originalUseQuery<Data, Err>(options);

  const onSuccess = useEffectEvent(() => {
    if (query.isSuccess) {
      options.onSuccess?.(query.data);
    }
  });
  const onData = useEffectEvent((data: Data) => options.onData?.(data));
  const onError = useEffectEvent((error: Err) => options.onError?.(error));

  useEffect(() => {
    if (query.isSuccess) {
      onSuccess();
    }
  }, [query.isSuccess]);

  useEffect(() => {
    if (query.data) {
      onData(query.data);
    }
  }, [query.data]);

  useEffect(() => {
    if (query.isError && query.error) {
      onError(query.error);
    }
  }, [query.isError, query.error]);

  return query;
};

// Use throughout the app instead of original `useQuery` from "@tanstack/react-query" module
export default useQuery;
